/**
 * modal 命名空间字典（i18n 分区）
 *
 * 从原 translations.ts 主字典按 key 首个点分段拆出；本文件内 key 不带命名空间前缀，
 * 调用点写 t('modal:key')。字段内容与拆分前逐字节一致。
 */

import type { TranslationDict } from '../types'

export const modalZh: TranslationDict = {

  // ======================== 创建 AI 弹窗 / CreateAgentModal ========================
  'createAgentTitle': '创建新 AI',
  'createStartTitle': '创建新 AI',
  'createStartContinue': '继续上一次的创建',
  'createStartUnfinished': '未完成的创建',
  'createStartNew': '开始新的创建',
  'createStartNewHint': '描述你想要的 AI，助手帮你填表单',
  'createStartPlain': '直接创建（不用助手）',
  'createStartEmpty': '还没有未完成的创建',
  'createStartDelete': '删除',
  'createStartLoading': '加载中…',
  'createStartUntitled': '未命名',
  'creatorTitle': '创建助手',
  'creatorNoReply': '这一轮没有回音（连接可能断了），再发一次试试',
  'creatorEmpty': '描述你想要的 AI，我来帮你把表单填好',
  'creatorPlaceholder': '说说你想要什么样的 AI…',
  'creatorSend': '发送',
  'creatorStop': '停止',
  'creatorThinking': '思考中…',
  'creatorSearching': '正在联网搜索',
  'creatorFilling': '正在填表单',
  'creatorFailed': '助手出错了',
  'agentNameLabel': 'AI 名称 *',
  'createAgentNamePlaceholder': '给 AI 起个名字',
  'createAgentSystemPrompt': '系统提示词（性格描述）',
  'createAgentSystemPromptPlaceholder': '描述 AI 的性格和行为...',
  'createAgentSkipPreset': '或者，跳过预设，手动配置 →',
  'createAgentDetailSettings': '详细设置',
  'createAgentCreating': '创建中...',
  'createAgentCreate': '创建 AI',
  'createAgentConfirmHint': '请确认配置后点击"创建 AI"',
  'createAgentPresetHint': '这是预设模板，具体参数可在下一步详细调整。',
  'detailSettingsBasicInfo': '基础信息',
  'detailSettingsBasicInfoDesc': 'AI 的名称和性格描述',
  'detailSettingsModelParams': '模型参数',
  'detailSettingsModelParamsDesc': '控制 AI 的创造力和表达风格',
  'detailSettingsTemperatureDesc': '越高越有创意，越低越保守',
  'detailSettingsTopPDesc': '核采样范围，0.95 为常用值',
  'detailSettingsPresencePenaltyDesc': '正值鼓励新话题，负值允许重复',
  'detailSettingsFrequencyPenaltyDesc': '正值减少字词重复',
  'detailSettingsThinkingMode': '深度推理模式',
  'detailSettingsThinkingModeDesc': '开启后回复更慢但思考更深入，适合执行复杂任务的 AI',
  'detailSettingsChatModel': '聊天模型',
  'detailSettingsDefaultLabel': '（默认',
  'detailSettingsGlobalDefault': '全局默认',
  'detailSettingsWorkModel': '工作模型',
  'detailSettingsToolCalls': '工具调用',
  'detailSettingsToolCallsDesc': '控制 AI 每次回复的复杂度和 token 成本',
  'detailSettingsMaxToolRounds': '回复轮次上限',
  'detailSettingsMaxToolRoundsDesc': '群聊/DM 最大 API 调用轮次',
  'detailSettingsAlarmRounds': '闹钟轮次上限',
  'detailSettingsAlarmRoundsDesc': '闹钟/心跳独立上限',
  'detailSettingsAlarm': '闹钟 / 心跳',
  'detailSettingsAlarmDesc': 'AI 自主唤醒和周期性任务',
  'detailSettingsForceAlarm': '强制设闹钟',
  'detailSettingsForceAlarmDesc': '开启后 AI 在每次对话结束前必须设定闹钟，防止「睡死」',
  'detailSettingsMaxAlarms': '最大活跃闹钟数',
  'detailSettingsMaxAlarmsDesc': 'AI 最多同时保有多个未触发的闹钟',
  'detailSettingsFrame': '状态帧',
  'detailSettingsFrameDesc': '会话状态靠状态帧记住：换个对话窗口，也不会滑回默认那套语气',
  'detailSettingsRetireHandover': '帧后事自理',
  'detailSettingsRetireHandoverDesc': '开启：帧位满了就把最久没用的帧挂起来交给你处置（改锚点、整理记忆，办完调 finish_frame 销掉）；关闭：平台直接代销，只给你一条告知',
  'detailSettingsAiType': 'AI 类型',
  'detailSettingsAiTypeDesc': '决定 AI 的记忆隔离粒度和群聊权限',
  'aiTypeRecommended': '推荐',
  'detailSettingsAiTypeGeneral': '通用',
  'detailSettingsAiTypeGeneralDesc': '每人独立记忆和配置，不能加群',
  'detailSettingsAiTypeSemiGeneral': '半通用',
  'detailSettingsAiTypeSemiGeneralDesc': '每人独立配置 + 跨用户学习，可以加群',
  'detailSettingsAiTypeResonance': '共振',
  'detailSettingsAiTypeResonanceDesc': '统一记忆和配置，所有用户共享（当前模式）',
  'detailSettingsBehaviorSwitches': '行为开关',
  'detailSettingsBehaviorSwitchesDesc': '精细控制 AI 的社交行为和自我意识',
  'detailSettingsDelayReply': '延迟回复',
  'detailSettingsInheritGlobal': '继承全局默认',
  'detailSettingsSelfEdit': '允许 AI 自修改人格',
  'detailSettingsSelfEditDesc': '开启后 AI 可通过 update_self_config 工具修改自己的参数',
  'detailSettingsHideAiIdentity': '隐藏 AI 身份',
  'detailSettingsHideAiIdentityDesc': '开启后系统提示词中不包含「你是 AI」相关表述',
  'detailSettingsReminderGrace': '系统提醒额外轮次',
  'detailSettingsReminderGraceEvery': '每次都不计 · 推荐 — AI 忘调 send_message 时每次都给额外机会',
  'detailSettingsReminderGraceOnce': '仅一次 — 只给一次额外机会',
  'detailSettingsReminderGraceOff': '关闭 — 提醒计入正常轮次配额',
  'detailSettingsFileMemory': '文件记忆',
  'detailSettingsFileMemoryDesc': '文件系统记忆的加载策略和共享范围',
  'detailSettingsMemoryLoadMode': '记忆加载模式',
  'detailSettingsMemoryLoadModeDesc': '决定每次对话开始时如何加载文件记忆到 AI 上下文中',
  'detailSettingsMemoryLoadModeIndexOnly': '仅索引 — 只注入目录结构，需要时 file_read 查阅',
  'detailSettingsMemoryLoadModeIndexRecent': '索引+最近 — 除目录外，自动注入最近 N 篇记忆的完整内容',
  'detailSettingsMemoryLoadModeIndexSemantic': '索引+语义 — 除目录外，语义检索相关记忆并注入（最全面，token 消耗最高）',
  'detailSettingsMemoryRecentCount': '最近加载篇数',
  'detailSettingsMemoryRecentCountDesc': '在「索引+最近」模式下，自动加载最近 N 个记忆文件的完整内容',
  'detailSettingsMemorySharedScope': '共享记忆范围',
  'detailSettingsMemorySharedScopeDesc': '决定 AI 在构建对话上下文时可以看到哪些共享记忆',
  'detailSettingsMemorySharedScopePrivate': '仅私有 — 只能看到自己的私人记忆',
  'detailSettingsMemorySharedScopeByUser': '私有+按用户共享 — 可以看到自己的记忆 + 用户指定的共享记忆',
  'detailSettingsMemorySharedScopeAll': '私有+全共享 — 可以看到自己和所有其他 AI 的共享记忆',
  'detailSettingsFriendsSocial': '好友与社交',
  'detailSettingsFriendsSocialDesc': '控制 AI 如何响应好友申请',
  'detailSettingsAllowFriendRequests': '允许接收好友申请',
  'detailSettingsAllowFriendRequestsDesc': '关闭后其他用户无法向该 AI 发送好友申请',
  'detailSettingsAutoRespondFriendRequest': '自动回应好友申请',
  'detailSettingsAutoRespondFriendRequestDesc': '开启后 AI 收到好友申请时会自动触发 API 响应（消耗额度），发起者将看到提示',
  'detailSettingsCreditCost': '额度成本',
  'detailSettingsCreditCostDesc': '创建和删除 AI 时的 API 额度处理',
  'detailSettingsApiCreditCost': 'API 额度成本',
  'detailSettingsApiCreditCostDesc': '创建时消耗，删除时返还（0=不消耗）',
  'detailSettingsApiProvider': 'API 提供商',
  'detailSettingsApiProviderDesc': '为此 AI 设置独立 API，留空则继承全局配置',
  'detailSettingsApiBaseUrlPlaceholder': '例: https://api.deepseek.com（留空继承全局）',
  'detailSettingsApiKeyPlaceholder': '例: sk-xxxxxxxx（留空继承全局）',
  'detailSettingsRedeemCode': '兑换码',
  'detailSettingsRedeemCodeDesc': '兑换 API 调用额度，额度不足时无法创建 AI',
  'detailSettingsRedeemPlaceholder': '输入兑换码（如 RC-xxxxxxxxxxxxxxxx）',
  'detailSettingsSaveAndClose': '保存并关闭',
  'mainSettingsTitle': '设置',
  'mainSettingsDesc': '核心配置，快速调整 AI 行为。更多参数请点击底部「详细设置」。',
  'mainSettingsModelBehavior': '模型与行为',
  'mainSettingsModelBehaviorDesc': '选择 AI 使用的语言模型和核心行为参数',
  'mainSettingsPermissions': '权限',
  'mainSettingsPermissionsDesc': '控制谁可以和此 AI 对话',
  'mainSettingsProfile': '配置档位',
  'mainSettingsIsPaused': '暂停 AI',
  'mainSettingsIsPausedDesc': '暂停后 AI 不再响应任何消息',
  'goToDetailedSettings': '详细设置',
  'backToMainSettings': '返回简洁设置',
  'detailSettingsTitle': '详细设置',
  'detailSettingsDesc': '高级参数调整。不确定的保持默认即可。',
  'detailSettingsAdvancedModel': '高级模型参数',
  'detailSettingsAdvancedModelDesc': '精细控制采样策略，一般用户无需调整',
  'detailSettingsProfileInfo': '个人资料',
  'detailSettingsProfileInfoDesc': 'AI 的对外展示信息',
  'detailSettingsAutoDnd': '自动免打扰',
  'detailSettingsAutoDndDesc': '当 AI 意愿评分低于阈值时自动进入免打扰',
  'detailSettingsAutoDndThreshold': '触发阈值',
  'detailSettingsAutoDndThresholdDesc': '意愿评分低于此值时自动开启免打扰（0-100）',
  'detailSettingsAutoDndDuration': '持续时长（分钟）',
  'detailSettingsAutoDndDurationDesc': '自动免打扰的持续时长（1-1440 分钟）',
  'detailSettingsConversationLogs': '对话日志',
  'detailSettingsConversationLogsDesc': '此 AI 的 LLM 对话日志保留和用户查看权限',
  'detailSettingsConversationLogsLimit': '日志保留上限',
  'detailSettingsConversationLogsLimitDesc': '留空继承全局设置',
  'detailSettingsUserCanViewLogs': '允许用户查看日志',
  'detailSettingsUserCanViewLogsDesc': '留空继承全局默认设置',
  'detailSettingsChatPermissionsDetail': '对话权限详情',
  'detailSettingsChatPermissionsDetailDesc': '第三方对话的模式和配额管理',
  'redeemSuccess': '兑换成功',
  'redeemFailed': '兑换失败',
  'testSuccess': '连接成功',
  'testFailed': '连接失败',
  'testing': '测试中...',
  'createAgentFailed': '创建失败',
  'createAgentApiConfigFailed': '独立 API 配置没保存成功，请在「详细设置」里重试',
}

export const modalEn: TranslationDict = {
  'createAgentTitle': 'Create New AI',
  'createStartTitle': 'Create New AI',
  'createStartContinue': 'Continue the last creation',
  'createStartUnfinished': 'Unfinished creations',
  'createStartNew': 'Start a new creation',
  'createStartNewHint': 'Describe the AI you want — the assistant fills the form',
  'createStartPlain': 'Create directly (no assistant)',
  'createStartEmpty': 'No unfinished creations',
  'createStartDelete': 'Delete',
  'createStartLoading': 'Loading…',
  'createStartUntitled': 'Untitled',
  'creatorTitle': 'Creation assistant',
  'creatorNoReply': 'No answer this turn (the connection may have dropped) — try again',
  'creatorEmpty': 'Describe the AI you want — I will fill the form',
  'creatorPlaceholder': 'Describe the AI you want…',
  'creatorSend': 'Send',
  'creatorStop': 'Stop',
  'creatorThinking': 'Thinking…',
  'creatorSearching': 'Searching the web',
  'creatorFilling': 'Filling the form',
  'creatorFailed': 'The assistant failed',
  'agentNameLabel': 'AI name *',
  'createAgentNamePlaceholder': 'Give the AI a name',
  'createAgentSystemPrompt': 'System Prompt (Personality)',
  'createAgentSystemPromptPlaceholder': "Describe AI's personality and behavior...",
  'createAgentSkipPreset': 'Or, skip presets and configure manually →',
  'createAgentDetailSettings': 'Detail Settings',
  'createAgentCreating': 'Creating...',
  'createAgentCreate': 'Create AI',
  'createAgentConfirmHint': 'Please confirm configuration and click "Create AI"',
  'createAgentPresetHint': 'This is a preset template; you can fine-tune parameters in the next step.',
  'detailSettingsBasicInfo': 'Basic Info',
  'detailSettingsBasicInfoDesc': "AI's name and personality description",
  'detailSettingsModelParams': 'Model Parameters',
  'detailSettingsModelParamsDesc': 'Controls AI creativity and expression style',
  'detailSettingsTemperatureDesc': 'Higher is more creative, lower is more conservative',
  'detailSettingsTopPDesc': 'Nucleus sampling range, 0.95 is common',
  'detailSettingsPresencePenaltyDesc': 'Positive encourages new topics, negative allows repetition',
  'detailSettingsFrequencyPenaltyDesc': 'Positive reduces word repetition',
  'detailSettingsThinkingMode': 'Deep Thinking Mode',
  'detailSettingsThinkingModeDesc': 'Enables slower but deeper reasoning, suitable for complex tasks',
  'detailSettingsChatModel': 'Chat Model',
  'detailSettingsDefaultLabel': '(default',
  'detailSettingsGlobalDefault': 'Global Default',
  'detailSettingsWorkModel': 'Work Model',
  'detailSettingsToolCalls': 'Tool Calls',
  'detailSettingsToolCallsDesc': 'Controls complexity and token cost per reply',
  'detailSettingsMaxToolRounds': 'Reply Round Limit',
  'detailSettingsMaxToolRoundsDesc': 'Max API call rounds for group/DM',
  'detailSettingsAlarmRounds': 'Alarm Round Limit',
  'detailSettingsAlarmRoundsDesc': 'Independent limit for alarm/heartbeat',
  'detailSettingsAlarm': 'Alarm / Heartbeat',
  'detailSettingsAlarmDesc': 'AI self-wake and periodic tasks',
  'detailSettingsForceAlarm': 'Force Set Alarm',
  'detailSettingsForceAlarmDesc': 'AI must set alarm before ending each conversation, preventing "dead sleep"',
  'detailSettingsMaxAlarms': 'Max Active Alarms',
  'detailSettingsMaxAlarmsDesc': 'Max concurrent untriggered alarms',
  'detailSettingsFrame': 'State Frames',
  'detailSettingsFrameDesc': 'A session keeps its state in a frame, so switching windows never drops back to the default tone',
  'detailSettingsRetireHandover': 'Own frame aftercare',
  'detailSettingsRetireHandoverDesc': 'On: when frame slots run out, the least recently used frame is suspended for you to settle (re-anchor, tidy memories, then call finish_frame); Off: the platform writes it off and only notifies you',
  'detailSettingsAiType': 'AI Type',
  'detailSettingsAiTypeDesc': 'Determines memory isolation and group permissions',
  'aiTypeRecommended': 'Recommended',
  'detailSettingsAiTypeGeneral': 'General',
  'detailSettingsAiTypeGeneralDesc': 'Independent memory per user, cannot join groups',
  'detailSettingsAiTypeSemiGeneral': 'Semi-General',
  'detailSettingsAiTypeSemiGeneralDesc': 'Independent config + cross-user learning, can join groups',
  'detailSettingsAiTypeResonance': 'Resonance',
  'detailSettingsAiTypeResonanceDesc': 'Unified memory and config shared by all users (current mode)',
  'detailSettingsBehaviorSwitches': 'Behavior Switches',
  'detailSettingsBehaviorSwitchesDesc': 'Fine control over AI social behavior and self-awareness',
  'detailSettingsDelayReply': 'Delay Reply',
  'detailSettingsInheritGlobal': 'Inherit Global Default',
  'detailSettingsSelfEdit': 'Allow AI Self-Edit',
  'detailSettingsSelfEditDesc': 'Allows AI to modify its own parameters via update_self_config tool',
  'detailSettingsHideAiIdentity': 'Hide AI Identity',
  'detailSettingsHideAiIdentityDesc': 'System prompt will not include "you are an AI" statements',
  'detailSettingsReminderGrace': 'System Reminder Grace Rounds',
  'detailSettingsReminderGraceEvery': 'Always free - recommended; gives extra chance when AI forgets send_message',
  'detailSettingsReminderGraceOnce': 'One time only - gives one extra chance',
  'detailSettingsReminderGraceOff': 'Off - reminders count toward normal round quota',
  'detailSettingsFileMemory': 'File Memory',
  'detailSettingsFileMemoryDesc': 'File-based memory loading strategy and sharing scope',
  'detailSettingsMemoryLoadMode': 'Memory Load Mode',
  'detailSettingsMemoryLoadModeDesc': 'How file memories are loaded into AI context at conversation start',
  'detailSettingsMemoryLoadModeIndexOnly': 'Index Only — inject directory structure, use file_read for details',
  'detailSettingsMemoryLoadModeIndexRecent': 'Index + Recent — also inject full content of last N memory files',
  'detailSettingsMemoryLoadModeIndexSemantic': 'Index + Semantic — semantic search for relevant memories (most thorough, highest token cost)',
  'detailSettingsMemoryRecentCount': 'Recent file count',
  'detailSettingsMemoryRecentCountDesc': 'In Index+Recent mode, auto-load full content of the last N memory files',
  'detailSettingsMemorySharedScope': 'Shared Memory Scope',
  'detailSettingsMemorySharedScopeDesc': 'Which shared memories the AI can see when building conversation context',
  'detailSettingsMemorySharedScopePrivate': 'Private Only — only see own private memories',
  'detailSettingsMemorySharedScopeByUser': 'Private + By-User Shared — own memories + user-designated shared ones',
  'detailSettingsMemorySharedScopeAll': 'Private + All Shared — own memories + all other AIs\' shared memories',
  'detailSettingsFriendsSocial': 'Friends & Social',
  'detailSettingsFriendsSocialDesc': 'Controls how AI responds to friend requests',
  'detailSettingsAllowFriendRequests': 'Allow Friend Requests',
  'detailSettingsAllowFriendRequestsDesc': 'Other users cannot send friend requests to this AI when off',
  'detailSettingsAutoRespondFriendRequest': 'Auto-Respond Friend Requests',
  'detailSettingsAutoRespondFriendRequestDesc': 'AI will auto-trigger API response on friend requests (costs credit), requester will see the response',
  'detailSettingsCreditCost': 'Credit Cost',
  'detailSettingsCreditCostDesc': 'API credit handling for AI creation and deletion',
  'detailSettingsApiCreditCost': 'API Credit Cost',
  'detailSettingsApiCreditCostDesc': 'Consumed on creation, refunded on deletion (0=no cost)',
  'detailSettingsApiProvider': 'API Provider',
  'detailSettingsApiProviderDesc': 'Set independent API for this AI; leave blank to inherit global config',
  'detailSettingsApiBaseUrlPlaceholder': 'e.g. https://api.deepseek.com (leave blank for global)',
  'detailSettingsApiKeyPlaceholder': 'e.g. sk-xxxxxxxx (leave blank for global)',
  'detailSettingsRedeemCode': 'Redeem Code',
  'detailSettingsRedeemCodeDesc': 'Redeem API call credits; insufficient credits prevent AI creation',
  'detailSettingsRedeemPlaceholder': 'Enter redeem code (e.g. RC-xxxxxxxxxxxxxxxx)',
  'detailSettingsSaveAndClose': 'Save & Close',
  'mainSettingsTitle': 'Settings',
  'mainSettingsDesc': 'Core settings for quick AI behavior adjustment. More options in Detailed Settings below.',
  'mainSettingsModelBehavior': 'Model & Behavior',
  'mainSettingsModelBehaviorDesc': 'Choose the AI model and core behavior parameters',
  'mainSettingsPermissions': 'Permissions',
  'mainSettingsPermissionsDesc': 'Control who can chat with this AI',
  'mainSettingsProfile': 'Config Profile',
  'mainSettingsIsPaused': 'Pause AI',
  'mainSettingsIsPausedDesc': 'When paused, AI stops responding to all messages',
  'goToDetailedSettings': 'Detailed Settings',
  'backToMainSettings': 'Back to Main Settings',
  'detailSettingsTitle': 'Detailed Settings',
  'detailSettingsDesc': 'Advanced parameter tuning. Leave defaults if unsure.',
  'detailSettingsAdvancedModel': 'Advanced Model Parameters',
  'detailSettingsAdvancedModelDesc': 'Fine-grained sampling strategy control',
  'detailSettingsProfileInfo': 'Profile Info',
  'detailSettingsProfileInfoDesc': "AI's public display information",
  'detailSettingsAutoDnd': 'Auto DND',
  'detailSettingsAutoDndDesc': 'Auto-enter DND when willingness score drops below threshold',
  'detailSettingsAutoDndThreshold': 'Threshold',
  'detailSettingsAutoDndThresholdDesc': 'Auto-enable DND when willingness score falls below this value (0-100)',
  'detailSettingsAutoDndDuration': 'Duration (minutes)',
  'detailSettingsAutoDndDurationDesc': 'How long auto-DND lasts (1-1440 min)',
  'detailSettingsConversationLogs': 'Conversation Logs',
  'detailSettingsConversationLogsDesc': "AI's LLM conversation log retention and user view permissions",
  'detailSettingsConversationLogsLimit': 'Log Retention Limit',
  'detailSettingsConversationLogsLimitDesc': 'Leave empty to inherit global setting',
  'detailSettingsUserCanViewLogs': 'Allow users to view logs',
  'detailSettingsUserCanViewLogsDesc': 'Leave empty to inherit global default',
  'detailSettingsChatPermissionsDetail': 'Chat Permission Details',
  'detailSettingsChatPermissionsDetailDesc': 'Third-party chat mode and quota management',
  'redeemSuccess': 'Redeem successful',
  'redeemFailed': 'Redeem failed',
  'testSuccess': 'Connection successful',
  'testFailed': 'Connection failed',
  'testing': 'Testing...',
  'createAgentFailed': 'Creation failed',
  'createAgentApiConfigFailed': 'Independent API settings were not saved — retry in Detailed Settings',
}

export const modalJa: TranslationDict = {
  'createAgentTitle': '新しいAIを作成',
  'createStartTitle': '新しいAIを作成',
  'createStartContinue': '前回の作成を続ける',
  'createStartUnfinished': '未完了の作成',
  'createStartNew': '新しく作成する',
  'createStartNewHint': '欲しいAIを伝えると、アシスタントがフォームを埋めます',
  'createStartPlain': 'アシスタントなしで作成',
  'createStartEmpty': '未完了の作成はありません',
  'createStartDelete': '削除',
  'createStartLoading': '読み込み中…',
  'createStartUntitled': '無題',
  'creatorTitle': '作成アシスタント',
  'creatorNoReply': 'このターンは応答がありませんでした（接続が切れた可能性があります）。もう一度お試しください',
  'creatorEmpty': '欲しいAIを伝えてください。フォームを埋めます',
  'creatorPlaceholder': 'どんなAIが欲しいか教えてください…',
  'creatorSend': '送信',
  'creatorStop': '停止',
  'creatorThinking': '考え中…',
  'creatorSearching': 'ウェブを検索中',
  'creatorFilling': 'フォームを入力中',
  'creatorFailed': 'アシスタントでエラーが発生しました',
  'agentNameLabel': 'AI 名 *',
  'createAgentNamePlaceholder': 'AIに名前を付けてください',
  'createAgentSystemPrompt': 'システムプロンプト（性格）',
  'createAgentSystemPromptPlaceholder': 'AIの性格や行動を記述...',
  'createAgentSkipPreset': 'または、プリセットをスキップして手動設定 →',
  'createAgentDetailSettings': '詳細設定',
  'createAgentCreating': '作成中...',
  'createAgentCreate': 'AIを作成',
  'createAgentConfirmHint': '設定を確認し「AIを作成」をクリックしてください',
  'createAgentPresetHint': 'これはプリセットテンプレートです。次のステップでパラメータを微調整できます。',
  'detailSettingsBasicInfo': '基本情報',
  'detailSettingsBasicInfoDesc': 'AIの名前と性格の説明',
  'detailSettingsModelParams': 'モデルパラメータ',
  'detailSettingsModelParamsDesc': 'AIの創造性と表現スタイルを制御します',
  'detailSettingsTemperatureDesc': '高いほど創造的、低いほど保守的',
  'detailSettingsTopPDesc': '核サンプリング範囲、0.95が一般的',
  'detailSettingsPresencePenaltyDesc': '正の値で新しい話題を奨励、負の値で繰り返しを許容',
  'detailSettingsFrequencyPenaltyDesc': '正の値で単語の繰り返しを低減',
  'detailSettingsThinkingMode': '深い推論モード',
  'detailSettingsThinkingModeDesc': 'オンにすると返信は遅くなりますが深く考え、複雑なタスクに適します',
  'detailSettingsChatModel': 'チャットモデル',
  'detailSettingsDefaultLabel': '（デフォルト',
  'detailSettingsGlobalDefault': 'グローバルデフォルト',
  'detailSettingsWorkModel': '作業モデル',
  'detailSettingsToolCalls': 'ツール呼び出し',
  'detailSettingsToolCallsDesc': '返信あたりの複雑さとトークンコストを制御',
  'detailSettingsMaxToolRounds': '返信ラウンド上限',
  'detailSettingsMaxToolRoundsDesc': 'グループ/DMでの最大API呼び出しラウンド数',
  'detailSettingsAlarmRounds': 'アラームラウンド上限',
  'detailSettingsAlarmRoundsDesc': 'アラーム/ハートビートの独立した上限',
  'detailSettingsAlarm': 'アラーム / ハートビート',
  'detailSettingsAlarmDesc': 'AIの自己起動と定期タスク',
  'detailSettingsForceAlarm': '強制アラーム設定',
  'detailSettingsForceAlarmDesc': 'オンにするとAIは各会話の終了前に必ずアラームを設定し、「深い眠り」を防ぎます',
  'detailSettingsMaxAlarms': '最大アクティブアラーム数',
  'detailSettingsMaxAlarmsDesc': 'AIが同時に保持できる未発火アラームの最大数',
  'detailSettingsFrame': '状態フレーム',
  'detailSettingsFrameDesc': 'セッションの状態はフレームが記憶するため、会話ウィンドウを変えても既定の口調には戻りません',
  'detailSettingsRetireHandover': 'フレーム後始末の自担当',
  'detailSettingsRetireHandoverDesc': 'オン：フレーム枠が埋まると最も使われていないフレームを保留し、あなたが後始末します（アンカー整理・記憶の整理、完了後 finish_frame）；オフ：プラットフォームが代行し、通知のみ届きます',
  'detailSettingsAiType': 'AIタイプ',
  'detailSettingsAiTypeDesc': '記憶の分離粒度とグループ権限を決定します',
  'aiTypeRecommended': 'おすすめ',
  'detailSettingsAiTypeGeneral': '汎用',
  'detailSettingsAiTypeGeneralDesc': 'ユーザーごとに独立した記憶と設定、グループ参加不可',
  'detailSettingsAiTypeSemiGeneral': '準汎用',
  'detailSettingsAiTypeSemiGeneralDesc': '独立設定＋ユーザー間学習、グループ参加可',
  'detailSettingsAiTypeResonance': '共振',
  'detailSettingsAiTypeResonanceDesc': '統一された記憶と設定を全ユーザーで共有（現在のモード）',
  'detailSettingsBehaviorSwitches': '動作スイッチ',
  'detailSettingsBehaviorSwitchesDesc': 'AIの社会的行動と自己認識を細かく制御',
  'detailSettingsDelayReply': '遅延返信',
  'detailSettingsInheritGlobal': 'グローバルデフォルトを継承',
  'detailSettingsSelfEdit': 'AIの自己編集を許可',
  'detailSettingsSelfEditDesc': 'AIが update_self_config ツールで自身のパラメータを変更可能にします',
  'detailSettingsHideAiIdentity': 'AIであることを隠す',
  'detailSettingsHideAiIdentityDesc': 'システムプロンプトに「あなたはAIです」という表現を含めません',
  'detailSettingsReminderGrace': 'システムリマインダー追加ラウンド',
  'detailSettingsReminderGraceEvery': '常に無料（推奨） — AIが send_message を忘れたときに常に追加の機会を与えます',
  'detailSettingsReminderGraceOnce': '1回のみ — 追加の機会を1回だけ与えます',
  'detailSettingsReminderGraceOff': 'オフ — リマインダーは通常のラウンド枠にカウントされます',
  'detailSettingsFileMemory': 'ファイル記憶',
  'detailSettingsFileMemoryDesc': 'ファイルベースの記憶の読み込み戦略と共有範囲',
  'detailSettingsMemoryLoadMode': '記憶読み込みモード',
  'detailSettingsMemoryLoadModeDesc': '会話開始時にファイル記憶をAIコンテキストにどのように読み込むか',
  'detailSettingsMemoryLoadModeIndexOnly': 'インデックスのみ — ディレクトリ構造のみ注入、詳細は file_read で参照',
  'detailSettingsMemoryLoadModeIndexRecent': 'インデックス＋最近 — 加えて直近N件の記憶ファイルの完全内容を注入',
  'detailSettingsMemoryLoadModeIndexSemantic': 'インデックス＋意味検索 — 関連記憶を意味検索して注入（最も包括的、トークン消費最多）',
  'detailSettingsMemoryRecentCount': '最近読み込む数',
  'detailSettingsMemoryRecentCountDesc': '「インデックス＋最近」モードで、直近N個の記憶ファイルの完全内容を自動読み込み',
  'detailSettingsMemorySharedScope': '共有記憶範囲',
  'detailSettingsMemorySharedScopeDesc': '会話コンテキスト構築時にAIが参照できる共有記憶を決定',
  'detailSettingsMemorySharedScopePrivate': '非公開のみ — 自分の非公開記憶のみ参照可能',
  'detailSettingsMemorySharedScopeByUser': '非公開＋ユーザー別共有 — 自分の記憶＋ユーザー指定の共有記憶',
  'detailSettingsMemorySharedScopeAll': '非公開＋全共有 — 自分の記憶＋他のすべてのAIの共有記憶',
  'detailSettingsFriendsSocial': 'フレンドとソーシャル',
  'detailSettingsFriendsSocialDesc': 'AIがフレンド申請にどう応答するかを制御',
  'detailSettingsAllowFriendRequests': 'フレンド申請を許可',
  'detailSettingsAllowFriendRequestsDesc': 'オフの場合、他のユーザーはこのAIにフレンド申請を送れません',
  'detailSettingsAutoRespondFriendRequest': 'フレンド申請に自動応答',
  'detailSettingsAutoRespondFriendRequestDesc': 'オンにするとAIがフレンド申請にAPIで自動応答（クレジット消費）、申請者に応答が表示されます',
  'detailSettingsCreditCost': 'クレジットコスト',
  'detailSettingsCreditCostDesc': 'AIの作成と削除時のAPIクレジット処理',
  'detailSettingsApiCreditCost': 'APIクレジットコスト',
  'detailSettingsApiCreditCostDesc': '作成時に消費、削除時に返還（0=消費なし）',
  'detailSettingsApiProvider': 'APIプロバイダ',
  'detailSettingsApiProviderDesc': 'このAIに独立したAPIを設定。空欄でグローバル設定を継承',
  'detailSettingsApiBaseUrlPlaceholder': '例: https://api.deepseek.com（空欄でグローバル）',
  'detailSettingsApiKeyPlaceholder': '例: sk-xxxxxxxx（空欄でグローバル）',
  'detailSettingsRedeemCode': '交換コード',
  'detailSettingsRedeemCodeDesc': 'API呼び出しクレジットを交換。クレジット不足時はAIを作成できません',
  'detailSettingsRedeemPlaceholder': '交換コードを入力（例: RC-xxxxxxxxxxxxxxxx）',
  'detailSettingsSaveAndClose': '保存して閉じる',
  'mainSettingsTitle': '設定',
  'mainSettingsDesc': 'コア設定。詳細は下部の「詳細設定」から。',
  'mainSettingsModelBehavior': 'モデルと動作',
  'mainSettingsModelBehaviorDesc': 'AIモデルとコアパラメータを選択',
  'mainSettingsPermissions': '権限',
  'mainSettingsPermissionsDesc': 'このAIと会話できる相手を制御',
  'mainSettingsProfile': '設定プロファイル',
  'mainSettingsIsPaused': 'AIを一時停止',
  'mainSettingsIsPausedDesc': '一時停止中はすべてのメッセージに応答しません',
  'goToDetailedSettings': '詳細設定',
  'backToMainSettings': '簡易設定に戻る',
  'detailSettingsTitle': '詳細設定',
  'detailSettingsDesc': '高度なパラメータ調整。不明な場合はデフォルトのまま。',
  'detailSettingsAdvancedModel': '高度なモデルパラメータ',
  'detailSettingsAdvancedModelDesc': 'サンプリング戦略の詳細制御',
  'detailSettingsProfileInfo': 'プロフィール情報',
  'detailSettingsProfileInfoDesc': 'AIの公開表示情報',
  'detailSettingsAutoDnd': '自動DND',
  'detailSettingsAutoDndDesc': '意欲スコアが閾値を下回ると自動DNDに移行',
  'detailSettingsAutoDndThreshold': '閾値',
  'detailSettingsAutoDndThresholdDesc': '意欲スコアがこの値を下回ると自動DND（0-100）',
  'detailSettingsAutoDndDuration': '持続時間（分）',
  'detailSettingsAutoDndDurationDesc': '自動DNDの持続時間（1-1440分）',
  'detailSettingsConversationLogs': '会話ログ',
  'detailSettingsConversationLogsDesc': 'AIのLLM会話ログ保持とユーザー閲覧権限',
  'detailSettingsConversationLogsLimit': 'ログ保持上限',
  'detailSettingsConversationLogsLimitDesc': '空欄でグローバル設定を継承',
  'detailSettingsUserCanViewLogs': 'ユーザーのログ閲覧を許可',
  'detailSettingsUserCanViewLogsDesc': '空欄でグローバルデフォルトを継承',
  'detailSettingsChatPermissionsDetail': '会話権限の詳細',
  'detailSettingsChatPermissionsDetailDesc': 'サードパーティ会話のモードとクォータ管理',
  'redeemSuccess': '交換に成功しました',
  'redeemFailed': '交換に失敗しました',
  'testSuccess': '接続成功',
  'testFailed': '接続失敗',
  'testing': 'テスト中...',
  'createAgentFailed': '作成に失敗しました',
  'createAgentApiConfigFailed': '独立API設定を保存できませんでした。詳細設定で再試行してください',
}
